import { useEffect, useState } from 'react';
import {
  Box, Card, Stack, Typography, Chip, Skeleton, Alert, Button, Divider,
  Dialog, DialogContent, IconButton, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ReceiptLongRounded as ReceiptIcon,
  CloseRounded as CloseIcon,
  PaymentsRounded as FinanceIcon,
  CalendarMonthRounded as DateIcon,
  PrintRounded as PrintIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import { P, money, HERO_BG, HERO_SHADOW, CARD_SHADOW } from './portalTheme';

// ─────────────────────────────────────────────────────────────────
// ReceiptsView — every receipt the school has issued for this learner.
//
// Lives in its own file because two places need exactly this and one
// of them would otherwise drift: Finance in the bottom bar, and
// Account → Receipts in the side menu. One component, one behaviour.
//
// Phone gets cards, desktop a table. Not decoration: a six-column
// table at 360px is unreadable, and a column of cards at 1200px throws
// away the width that makes a history scannable.
// ─────────────────────────────────────────────────────────────────

const PAGE = 40;

function DetailRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <Stack direction="row" spacing={2} sx={{ py: 1 }}>
      <Typography sx={{ fontSize: 12.5, color: P.inkSoft, width: 118, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: P.ink, flex: 1, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Stack>
  );
}

// The receipt itself, as the parent would have been handed it.
function ReceiptDialog({ payment, student, onClose }) {
  if (!payment) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: P.brand, px: 3, pt: 3, pb: 2.5, position: 'relative' }}>
        <IconButton onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: P.onBrand }}>
          <CloseIcon />
        </IconButton>
        <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: .8,
          textTransform: 'uppercase', color: P.onBrandSoft }}>
          {payment.type || 'Payment'}
        </Typography>
        <Typography sx={{ fontSize: 32, fontWeight: 900, color: P.onBrand, lineHeight: 1.15,
          fontVariantNumeric: 'tabular-nums' }}>
          GHS {money(payment.amount)}
        </Typography>
        <Typography sx={{ fontSize: 13, color: P.onBrandSoft, mt: .3 }}>
          {payment.date}
        </Typography>
      </Box>
      <DialogContent sx={{ px: 3, py: 2 }}>
        {student?.name && <DetailRow label="Student" value={`${student.name}${student.cla ? ` · ${student.cla}` : ''}`} />}
        <DetailRow label="Receipt no." value={payment.receipt_no} />
        <Divider />
        <DetailRow label="Paid for"    value={payment.type} />
        <DetailRow label="Term"        value={[payment.term, payment.year].filter(Boolean).join(' · ')} />
        <DetailRow label="Class"       value={payment.cla} />
        <DetailRow label="Method"      value={payment.mode} />
        <DetailRow label="Received by" value={payment.received_by} />
        <DetailRow label="Note"        value={payment.note} />
        {/* Only meaningful when the cashier recorded one. A bare 0.00
            would read as "you owe nothing", which it does not mean. */}
        {payment.balance_after > 0 && (
          <>
            <Divider />
            <DetailRow label="Balance after" value={`GHS ${money(payment.balance_after)}`} />
          </>
        )}
        <Button fullWidth startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{
            mt: 2, fontWeight: 800, textTransform: 'none', borderRadius: 2.5,
            bgcolor: P.brandSoft, color: P.brandDeep,
            '&:hover': { bgcolor: '#D6E6F8' },
          }}>
          Print this receipt
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function ReceiptsView({ student, showHeader = true }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [data, setData]       = useState(null);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore]       = useState(false);
  const [error, setError]     = useState('');
  const [type, setType]       = useState('');
  const [open, setOpen]       = useState(null);

  const fetchPage = (offset, typeKey) =>
    API.get('/portal/payments', { params: { limit: PAGE, offset, type: typeKey || undefined } });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPage(0, type)
      .then(r => { if (alive) { setData(r.data); setRows(r.data?.payments || []); setError(''); } })
      .catch(e => { if (alive) setError(e?.response?.data?.message || 'Could not load your receipts'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [type]);

  const loadMore = async () => {
    setMore(true);
    try {
      const r = await fetchPage(rows.length, type);
      setRows(prev => [...prev, ...(r.data?.payments || [])]);
    } catch { /* the button stays; nothing is lost */ }
    finally { setMore(false); }
  };

  const types = data?.types || [];

  return (
    <Box>
      {showHeader && (
        <Card sx={{
          borderRadius: 4.5, p: { xs: 2.4, md: 2.8 }, mb: 2,
          backgroundImage: HERO_BG, boxShadow: HERO_SHADOW,
        }}>
          <Stack direction="row" alignItems="center" spacing={1.6}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 3, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,.18)', color: P.onBrand,
            }}>
              <FinanceIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: .6,
                textTransform: 'uppercase', color: P.onBrandSoft }}>
                {type ? `${types.find(t => t.key === type)?.label || 'Filtered'} paid` : 'Total paid to date'}
              </Typography>
              <Typography sx={{
                fontSize: { xs: 29, md: 33 }, fontWeight: 900, color: P.onBrand, lineHeight: 1.1,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {loading ? '—' : `GHS ${money(
                  type ? (types.find(t => t.key === type)?.total || 0) : (data?.amount || 0)
                )}`}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: P.onBrandSoft, mt: .2 }}>
                {loading ? 'loading…' : `${data?.total || 0} receipt${data?.total === 1 ? '' : 's'} on record`}
              </Typography>
            </Box>
          </Stack>
        </Card>
      )}

      {types.length > 1 && (
        <Stack direction="row" spacing={0.8} sx={{
          mb: 2, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { height: 5 },
        }}>
          <Chip label="All" onClick={() => setType('')}
            variant={type ? 'outlined' : 'filled'}
            sx={{ fontWeight: 700, flexShrink: 0,
              bgcolor: type ? 'transparent' : P.ink, color: type ? P.ink : '#fff' }} />
          {types.map(t => (
            <Chip key={t.key} label={`${t.label} · ${t.count}`} onClick={() => setType(t.key)}
              variant={type === t.key ? 'filled' : 'outlined'}
              sx={{ fontWeight: 700, flexShrink: 0,
                bgcolor: type === t.key ? P.ink : 'transparent',
                color: type === t.key ? '#fff' : P.ink }} />
          ))}
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ borderRadius: 3, mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack spacing={1.2}>
          {[0, 1, 2, 3, 4].map(i => (
            <Skeleton key={i} variant="rounded" height={isDesktop ? 52 : 68} sx={{ borderRadius: 3 }} />
          ))}
        </Stack>
      ) : rows.length === 0 ? (
        <Card sx={{
          borderRadius: 4, p: 4, textAlign: 'center', boxShadow: 'none',
          border: '1px dashed', borderColor: P.line,
        }}>
          <ReceiptIcon sx={{ fontSize: 40, color: P.line }} />
          <Typography sx={{ fontWeight: 800, color: P.ink, mt: 1 }}>
            No receipts {type ? 'of this kind ' : ''}yet
          </Typography>
          <Typography sx={{ fontSize: 13, color: P.inkSoft, mt: .5 }}>
            They appear here as soon as the school records a payment.
          </Typography>
        </Card>
      ) : isDesktop ? (
        <Card sx={{ borderRadius: 3.5, boxShadow: CARD_SHADOW, border: '1px solid', borderColor: P.line }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{
                  '& th': {
                    bgcolor: '#FAFBFC', color: P.inkSoft, fontWeight: 800, fontSize: 11,
                    letterSpacing: .5, textTransform: 'uppercase',
                    borderBottom: '1px solid', borderColor: P.line,
                  },
                }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Paid for</TableCell>
                  <TableCell>Term</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover onClick={() => setOpen(r)}
                    sx={{ cursor: 'pointer', '& td': { fontSize: 13, py: 1.1 } }}>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {r.date}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.receipt_no}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.type}</TableCell>
                    <TableCell sx={{ color: P.inkSoft }}>
                      {[r.term, r.year].filter(Boolean).join(' · ') || '—'}
                    </TableCell>
                    <TableCell sx={{ color: P.inkSoft }}>{r.mode || '—'}</TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 800, color: P.good, fontVariantNumeric: 'tabular-nums',
                    }}>
                      {money(r.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        <Stack spacing={1.2}>
          {rows.map(r => (
            <Card key={r.id} onClick={() => setOpen(r)} sx={{
              borderRadius: 3.5, px: 1.8, py: 1.5, cursor: 'pointer', boxShadow: CARD_SHADOW,
              border: '1px solid', borderColor: P.line,
              transition: 'border-color .15s',
              '&:active': { bgcolor: P.brandFaint },
              '&:hover': { borderColor: P.brand },
            }}>
              <Stack direction="row" alignItems="center" spacing={1.4}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 2.5, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: P.brandSoft, color: P.brandDeep,
                }}>
                  <ReceiptIcon sx={{ fontSize: 21 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: P.ink }}>
                    {r.type || 'Payment'}
                  </Typography>
                  <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: .2 }}>
                    <DateIcon sx={{ fontSize: 13, color: P.inkSoft }} />
                    <Typography sx={{ fontSize: 11.5, color: P.inkSoft }}>
                      {r.date} · {r.receipt_no}
                    </Typography>
                  </Stack>
                </Box>
                <Typography sx={{
                  fontSize: 15, fontWeight: 800, color: P.good,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {money(r.amount)}
                </Typography>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {!loading && rows.length > 0 && rows.length < (data?.total || 0) && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Button onClick={loadMore} disabled={more} variant="outlined"
            sx={{
              fontWeight: 800, textTransform: 'none', borderRadius: 3, px: 3,
              borderColor: P.line, color: P.ink,
              '&:hover': { borderColor: P.brand, bgcolor: P.brandSoft },
            }}>
            {more ? 'Loading…' : `Show more (${(data?.total || 0) - rows.length} left)`}
          </Button>
        </Stack>
      )}

      {!loading && rows.length > 0 && (
        <Typography sx={{ mt: 2, textAlign: 'center', fontSize: 12, color: P.inkSoft }}>
          Showing {rows.length} of {data?.total || 0}
        </Typography>
      )}

      <ReceiptDialog payment={open} student={student} onClose={() => setOpen(null)} />
    </Box>
  );
}
